/**
 * Pure validation of a draft TopologySnapshot before it's persisted.
 *
 * Invariants (matching `Topology.constructor` in domain/Topology.ts plus
 * the operator-facing rule cpMaxPowerW ≥ Σ EVSE maxPowerW):
 *   - at least one EVSE
 *   - every EVSE has a non-empty name
 *   - every EVSE has maxPowerW > 0
 *   - every EVSE has ≥1 connector
 *   - connector ids are positive integers
 *   - connector ids are globally unique across the whole station
 *   - cpMaxPowerW > 0
 *   - cpMaxPowerW ≥ Σ EVSE maxPowerW (operator policy, evse2 parity)
 *
 * Returns the list of human-readable issues; empty list = valid.
 */
import type { TopologySnapshot } from './Topology';

export function validateTopology(snap: TopologySnapshot): readonly string[] {
  const issues: string[] = [];
  if (snap.evses.length < 1) {
    issues.push('At least one EVSE is required.');
  }
  if (!(snap.cpMaxPowerW > 0)) {
    issues.push('cpMaxPowerW must be > 0.');
  }
  const seenConnectors = new Set<number>();
  let summedEvsePower = 0;
  for (const evse of snap.evses) {
    summedEvsePower += evse.maxPowerW;
    if (evse.name.length === 0) {
      issues.push(`EVSE #${evse.id}: name is required.`);
    }
    if (!(evse.maxPowerW > 0)) {
      issues.push(`EVSE #${evse.id}: maxPowerW must be > 0.`);
    }
    if (evse.connectorIds.length < 1) {
      issues.push(`EVSE #${evse.id}: at least one connector is required.`);
    }
    for (const cid of evse.connectorIds) {
      if (!Number.isInteger(cid) || cid < 1) {
        issues.push(`EVSE #${evse.id}: connector ${cid} is not a positive integer.`);
        continue;
      }
      if (seenConnectors.has(cid)) {
        issues.push(`Connector ${cid} appears in more than one EVSE — must be unique.`);
      } else {
        seenConnectors.add(cid);
      }
    }
  }
  if (snap.cpMaxPowerW > 0 && summedEvsePower > snap.cpMaxPowerW) {
    issues.push(
      `cpMaxPowerW (${snap.cpMaxPowerW}) must be ≥ Σ EVSE maxPowerW (${summedEvsePower}).`,
    );
  }
  return issues;
}
